import * as React from 'react';
import { IntlProvider as DefaultIntlProvider } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@creditornot/cb-button';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { PageSpinner } from 'common/components/cookbook/PageSpinner';
import { Stack } from 'common/components/cookbook/Stack';
import { useUserLanguage } from 'common/hooks/useUserLanguage';
import { useUserLocale } from 'common/hooks/useUserLocale';
import { LocalizedMessageLanguage } from 'common/types';
import { DEFAULT_LANGUAGE, DEFAULT_LOCALE } from 'common/utils/localeUtils';

import { messagesLoaders } from '../localized-messages';

const LOADING_TIMEOUT_MS = 5000;

type MessagesState = {
  messages: Record<string, string> | null;
  hasError: boolean;
};

const useMessages = (language: LocalizedMessageLanguage): MessagesState => {
  const [state, setState] = React.useState<MessagesState>({ messages: null, hasError: false });

  React.useEffect(() => {
    let cancelled = false;
    setState({ messages: null, hasError: false });

    // Fallback timeout to prevent infinite loading state
    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        setState((current) => {
          // Only show error if still in initial loading state
          if (current.messages === null && !current.hasError) {
            console.error('Locale messages loading timed out');
            return { messages: null, hasError: true };
          }
          return current;
        });
      }
    }, LOADING_TIMEOUT_MS);

    const loaders = [messagesLoaders[DEFAULT_LANGUAGE]];
    if (language !== DEFAULT_LANGUAGE) {
      loaders.push(messagesLoaders[language]);
    }
    const promises = loaders.map((loader) => loader().then((loaded) => loaded.default));
    Promise.all(promises)
      .then((translations) => {
        if (!cancelled) {
          const combinedMessages = translations.reduce(
            (acc, translation) => ({ ...acc, ...translation }),
            {},
          );
          setState({ messages: combinedMessages, hasError: false });
        }
      })
      .catch((err) => {
        console.error('Failed to load locale messages:', err);
        if (!cancelled) {
          setState({ messages: null, hasError: true });
        }
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [language]);

  return state;
};

const ErrorRoot = styled(Stack)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  flex: 1;
  text-align: center;
  padding: 24px;
`;

const ErrorTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

const ErrorMessage = styled.p`
  font-size: 16px;
  max-width: 400px;
  margin: 0;
  color: ${color.textSubdued};
`;

const LocaleLoadError: React.FC = () => (
  <ErrorRoot direction="column" alignItems="center" justifyContent="center" gap="regularXlarge">
    <ErrorTitle>Failed to load application</ErrorTitle>
    <ErrorMessage>
      The application could not load required translations. Please check your internet connection
      and try again.
    </ErrorMessage>
    <Button size="small" variant="primary" onClick={() => window.location.reload()}>
      Reload page
    </Button>
  </ErrorRoot>
);

export const IntlProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const userLocale = useUserLocale();

  const userLanguage = useUserLanguage();
  const { messages, hasError } = useMessages(userLanguage);

  if (hasError) {
    return <LocaleLoadError />;
  }

  if (messages === null) {
    return <PageSpinner />;
  }

  return (
    <DefaultIntlProvider
      messages={messages}
      locale={userLocale}
      defaultLocale={DEFAULT_LOCALE}
      defaultRichTextElements={{
        b: (chunks) => <b>{chunks}</b>,
        pre: (chunks) => <pre>{chunks}</pre>,
      }}
    >
      {children}
    </DefaultIntlProvider>
  );
};
